import { getPeriodStartEnd } from '@dolfin/utils/dates'
import { DolfinDiagram } from '../diagram'
import Metrics from '../domains/metrics'
import Tvp from '../domains/tvp'
import DolfinCompensationRulesFlowExecutor from '../flow-executor/compensation-rules'
import { CommissionOutput, CommissionOutputField, NodeError, NodeWarning } from '../types'
import { getCompensationValue } from '../utils'
import { DolfinNode } from './base'
import {
  CommissionFormDataTiered,
  CommissionFormDataTieredMetric,
  CommissionFormDataTieredTarget,
  hasCalculateOverColumn,
  hasCalculateOverMetric,
  isFieldCommission,
  isPieceworkCommission,
  isStraightCommission,
  isTieredCommissionWithColumn,
  isTieredCommissionWithTargets,
  MetricRef,
  TypedNode
} from './types'

/**
 * Commission node - calculates commission amounts based on record or metric values.
 *
 * Supports calculation types:
 * - `straight` - Fixed percentage of a value
 * - `tiered` - Variable percentage based on attainment tiers
 * - `piecework` - Fixed amount per record
 * - `field` - Percentage from a record field
 *
 * @see /docs/domain/nodes/commission.md for canonical documentation
 */
export class CommissionNode extends DolfinNode<DolfinCompensationRulesFlowExecutor, 'commission'> {
  private async getTargetCreditedMetricScope(profileId: string, referenceDate: string) {
    const targetId = (this.formData as CommissionFormDataTieredTarget).targets?.[0]?.id
    if (!targetId) return null

    return this.flowExecutor.getTargetCreditedMetricScope(targetId, profileId, referenceDate)
  }

  private async getCreditSetupTargetScope(referenceDate: string) {
    const profileId = this.getProfileId()
    if (!profileId) return undefined

    const targetScope = await this.getTargetCreditedMetricScope(profileId, referenceDate)
    return targetScope?.useTargetCreditedMetricAggregation ? targetScope : undefined
  }

  async process(): Promise<CommissionOutput> {
    if (
      this.flowExecutor.compensationRule.compensation_rule_versions?.[0]?.is_tvp_based &&
      isStraightCommission(this.formData)
    ) {
      return await this.processTvpBasedStraightCommission()
    }

    if (
      !this.flowExecutor.compensationRule.outcome_period &&
      (isStraightCommission(this.formData) || isFieldCommission(this.formData)) &&
      hasCalculateOverColumn(this.formData)
    ) {
      const percentage =
        isFieldCommission(this.formData) && this.formData.percentage_column
          ? (this.flowExecutor.recordData!.getFieldValue(
              this.formData.percentage_column.name as string,
              this.flowExecutor.customFields
            ) as number) / 100
          : this.formData.percentage!

      return [
        {
          name: 'commission_percentage',
          value: percentage
        },
        {
          name: 'compensation_value',
          value:
            percentage *
            ((this.flowExecutor.recordData!.getFieldValue(
              this.formData.calculate_over_column.name as string,
              this.flowExecutor.customFields
            ) || 0) as number)
        },
        {
          name: 'field_value',
          value:
            (this.flowExecutor.recordData!.getFieldValue(
              this.formData.calculate_over_column.name as string,
              this.flowExecutor.customFields
            ) as number) || 0
        }
      ]
    }

    if (this.formData.type === 'tiered') {
      if (this.flowExecutor.compensationRule.compensation_rule_versions?.[0]?.is_tvp_based) {
        return await this.processTvpBasedTieredCommission()
      }
      return await this.processTiered()
    }

    if (
      this.flowExecutor.compensationRule.outcome_period &&
      isStraightCommission(this.formData) &&
      hasCalculateOverMetric(this.formData)
    ) {
      return await this.processStraightPeriodCommission()
    }

    if (isPieceworkCommission(this.formData)) {
      return [
        {
          name: 'compensation_value',
          value: this.formData.fixed_amount ?? 0
        }
      ]
    }

    return [
      {
        name: 'compensation_value',
        value: 0
      }
    ]
  }

  private async processStraightPeriodCommission(): Promise<CommissionOutput> {
    if (!isStraightCommission(this.formData) || !hasCalculateOverMetric(this.formData)) {
      return [{ name: 'compensation_value', value: 0 }]
    }

    const percentage = this.formData.percentage as number
    const period = this.getComputationPeriod()
    const targetScope = await this.getCreditSetupTargetScope(period.end)
    const metricValue = await this.getMetricReferenceValue(
      this.formData.calculate_over_metric,
      period.start,
      period.end,
      undefined,
      targetScope
    )

    const valueBeforeRegulation = percentage * metricValue
    let previousPayoutsTotal = 0
    let periodCandidateValue: number | undefined
    let periodComparisonData:
      | {
          field_value: number
          compensation_value: number
          period: { start: string; end: string }
        }
      | undefined

    if (this.hasLongTermBalancing()) {
      const previousPayouts = await this.getPreviousPayoutsForLongTermBalancing(
        this.flowExecutor.profileId
      )
      previousPayoutsTotal = this.sumPreviousPayoutsForLongTermBalancing(previousPayouts)

      if (this.shouldApplyLongTermOnlyIfHigher()) {
        const currentPeriod = this.getComputationPeriod(true)
        const nonLtbMetricValue = await this.getMetricReferenceValue(
          this.formData.calculate_over_metric,
          currentPeriod.start,
          currentPeriod.end,
          undefined,
          targetScope
        )
        const nonLtbValue = percentage * nonLtbMetricValue
        periodComparisonData = {
          field_value: nonLtbMetricValue,
          compensation_value: nonLtbValue,
          period: currentPeriod
        }
        periodCandidateValue = nonLtbValue
      }
    }

    const balancingResults: CommissionOutputField[] = this.hasLongTermBalancing()
      ? [
          {
            name: 'total_previous_payouts',
            value: previousPayoutsTotal
          },
          {
            name: 'compensation_value_before_regulation',
            value: valueBeforeRegulation
          },
          {
            name: 'regulation_period',
            value: this.flowExecutor.compensationRule.long_term_balancing_period
          },
          ...(periodComparisonData
            ? [
                {
                  name: 'compensation_value_comparison',
                  value: {
                    long_term_balancing: {
                      field_value: metricValue,
                      compensation_value_before_regulation: valueBeforeRegulation,
                      compensation_value: valueBeforeRegulation - previousPayoutsTotal,
                      period
                    },
                    period: periodComparisonData
                  }
                } satisfies CommissionOutputField
              ]
            : [])
        ]
      : []

    return [
      {
        name: 'commission_percentage',
        value: percentage
      },
      {
        name: 'compensation_value',
        value: valueBeforeRegulation
      },
      ...(periodCandidateValue !== undefined
        ? [
            {
              name: 'compensation_value_period_candidate',
              value: periodCandidateValue
            } satisfies CommissionOutputField
          ]
        : []),
      {
        name: 'field_value',
        value: metricValue
      },
      ...balancingResults,
      {
        name: 'context',
        value: {
          attainment: metricValue,
          metric: this.formData.calculate_over_metric,
          period
        }
      }
    ]
  }

  private getProcessingDate() {
    if (this.flowExecutor.compensationRule.outcome_period) {
      return this.flowExecutor.period!.end
    }

    return this.getRecordProcessingDate()
  }

  private getTargetIdByEvaluationMode(
    target: CommissionFormDataTieredTarget['targets'][number],
    evaluationMode: 'ltb' | 'period_candidate'
  ) {
    return evaluationMode === 'period_candidate'
      ? (target.period_candidate_target_id ?? target.id)
      : target.id
  }

  private getTargetEvaluationPeriod(
    target: CommissionFormDataTieredTarget['targets'][number],
    period?: { start: string; end: string },
    evaluationMode: 'ltb' | 'period_candidate' = 'ltb'
  ) {
    if (
      period &&
      evaluationMode === 'ltb' &&
      this.hasLongTermBalancing() &&
      this.shouldApplyLongTermOnlyIfHigher() &&
      target.period_candidate_target_id !== undefined
    ) {
      return this.getComputationPeriod(true)
    }

    return period
  }

  private async getTargetAchievement(
    period?: { start: string; end: string },
    evaluationMode: 'ltb' | 'period_candidate' = 'ltb',
    includeCurrentRecord?: boolean,
    sourceIdLimitOnLastDay?: number
  ) {
    const formData = this.formData as CommissionFormDataTieredTarget
    let attainment = 0

    for (const target of formData.targets) {
      const targetId = this.getTargetIdByEvaluationMode(target, evaluationMode)
      const evaluationPeriod = this.getTargetEvaluationPeriod(target, period, evaluationMode)
      const achievement = await this.getPeriodTargetAchievement(
        targetId,
        evaluationPeriod?.start,
        undefined,
        includeCurrentRecord ?? !!formData.full_period_achievement,
        sourceIdLimitOnLastDay
      )
      const targetAch =
        achievement.achievement > 1 && !!formData.cap_target_achievement
          ? 1
          : achievement.achievement

      attainment +=
        !!formData.use_target_weights && formData.targets.length > 1
          ? targetAch * (target.weight ?? 1)
          : targetAch
    }

    if (formData.targets.length > 1 && !formData.use_target_weights) {
      attainment /= formData.targets.length ?? 1
    }

    return attainment
  }

  private calculateMarginalCommissionTiering(args: {
    attainmentBefore: number
    attainmentAfter: number
    fieldValue: number
    tiers: CommissionFormDataTiered['tiers']
  }) {
    const { attainmentBefore, attainmentAfter, fieldValue } = args
    const totalAttainmentMovement = attainmentAfter - attainmentBefore

    if (totalAttainmentMovement <= 0 || fieldValue === 0) return []

    const tiers = [...args.tiers].sort((a, b) => a.attainment - b.attainment)

    const marginalTiers = []

    for (const [tierIndex, tier] of tiers.entries()) {
      const nextTier = tiers[tierIndex + 1]
      const bandStart = tierIndex === 0 ? Number.NEGATIVE_INFINITY : tier.attainment
      const bandEnd = nextTier?.attainment ?? Number.POSITIVE_INFINITY
      const attainmentStart = Math.max(attainmentBefore, bandStart)
      const attainmentEnd = Math.min(attainmentAfter, bandEnd)
      const bandMovement = attainmentEnd - attainmentStart

      if (bandMovement <= 0) continue

      const configuredRate =
        tier.value ?? (tier.percentage == null ? undefined : Number(tier.percentage) * 100)
      if (configuredRate == null) return []

      let commissionPercentage: number
      try {
        commissionPercentage = getCompensationValue(configuredRate, attainmentAfter) / 100
      } catch {
        return []
      }

      if (!Number.isFinite(commissionPercentage)) return []

      const allocatedFieldValue = fieldValue * (bandMovement / totalAttainmentMovement)

      marginalTiers.push({
        tier_index: tierIndex,
        attainment_start: attainmentStart,
        attainment_end: attainmentEnd,
        field_value: allocatedFieldValue,
        commission_percentage: commissionPercentage,
        compensation_value: allocatedFieldValue * commissionPercentage
      })
    }

    return marginalTiers
  }

  private async getMetricAchievement(period?: { start: string; end: string }) {
    const formData = this.formData as CommissionFormDataTieredMetric

    if (this.flowExecutor.compensationRule.outcome_period && period) {
      return this.getMetricReferenceValue(formData.metric, period.start, period.end)
    }

    const processingDate = this.getProcessingDate()
    if (!processingDate || !formData.metric_period) {
      return 0
    }

    const metricPeriod = getPeriodStartEnd(formData.metric_period, new Date(processingDate))

    return this.getMetricReferenceValue(
      formData.metric,
      metricPeriod.start,
      formData.full_period_achievement ? metricPeriod.end : processingDate,
      !this.flowExecutor.compensationRule.outcome_period && !formData.full_period_achievement
        ? this.flowExecutor.recordData!.record.id
        : undefined
    )
  }

  private async getAttainment(
    period?: { start: string; end: string },
    evaluationMode: 'ltb' | 'period_candidate' = 'ltb'
  ) {
    const processingDate = this.getProcessingDate()

    if (!processingDate) {
      console.warn(
        'Processing date not found: ',
        this.flowExecutor.compensationRule.accrual_date_field
      )
      return 0
    }

    if (isTieredCommissionWithTargets(this.formData)) {
      return await this.getTargetAchievement(period, evaluationMode)
    }

    if (
      this.formData.type === 'tiered' &&
      this.formData.reference_type === 'metric' &&
      'metric' in this.formData &&
      !!this.formData.metric
    ) {
      return await this.getMetricAchievement(period)
    }

    if (isTieredCommissionWithColumn(this.formData)) {
      return this.flowExecutor.recordData!.getFieldValue(
        this.formData.column.name as string,
        this.flowExecutor.customFields
      ) as number
    }

    return 0
  }

  private async processTiered(): Promise<CommissionOutput> {
    const period = this.flowExecutor.period ? this.getComputationPeriod() : undefined
    let attainment = 0
    const processingDate = this.getProcessingDate()

    if (!processingDate) {
      console.warn(
        'Processing date not found: ',
        this.flowExecutor.compensationRule.accrual_date_field
      )
      return [
        {
          name: 'compensation_value',
          value: 0
        }
      ]
    }

    attainment = await this.getAttainment(period, 'ltb')
    attainment = isTieredCommissionWithTargets(this.formData) ? attainment * 100 : attainment

    const isMarginalCommissionTiering =
      !this.flowExecutor.compensationRule.outcome_period &&
      isTieredCommissionWithTargets(this.formData) &&
      hasCalculateOverColumn(this.formData) &&
      !this.formData.full_period_achievement &&
      this.formData.marginal_commission_tiering === true
    let attainmentBefore = attainment

    if (isMarginalCommissionTiering) {
      // The normal record-based lookup is bounded by the current record and
      // therefore represents attainment after it. To obtain the prior
      // attainment, bound the same-day source set just before this record.
      const recordId = this.flowExecutor.recordData!.record.id
      attainmentBefore = (await this.getTargetAchievement(period, 'ltb', false, recordId - 1)) * 100
    }

    const tierFormData = this.formData as CommissionFormDataTiered & {
      tiers_without_ltb?: CommissionFormDataTiered['tiers']
    }
    const tiers = [...tierFormData.tiers].sort((a, b) => b.attainment - a.attainment)

    for (const tier of tiers) {
      if (
        attainment >= tier.attainment ||
        (attainment < tier.attainment && tier.attainment === 0)
      ) {
        const fieldValue = await this.getTieredFieldValue(period)

        if (!tier.value && !tier.percentage) {
          return [
            {
              name: 'compensation_value',
              value: 0
            }
          ]
        }

        const ltbTiers = [...tierFormData.tiers].sort((a, b) => b.attainment - a.attainment)
        const nonLtbTiers = [...(tierFormData.tiers_without_ltb ?? tierFormData.tiers)].sort(
          (a, b) => b.attainment - a.attainment
        )

        let commissionPercentage =
          getCompensationValue(tier.value ?? Number(tier.percentage) * 100, attainment) / 100
        const marginalCommissionTiering = isMarginalCommissionTiering
          ? this.calculateMarginalCommissionTiering({
              attainmentBefore,
              attainmentAfter: attainment,
              fieldValue,
              tiers: tierFormData.tiers
            })
          : []
        let valueBeforeRegulation = commissionPercentage * fieldValue

        if (marginalCommissionTiering.length > 0) {
          valueBeforeRegulation = marginalCommissionTiering.reduce(
            (total, segment) => total + segment.compensation_value,
            0
          )
          commissionPercentage = fieldValue === 0 ? 0 : valueBeforeRegulation / fieldValue
        } else if (isMarginalCommissionTiering && fieldValue === 0) {
          commissionPercentage = 0
        }
        let previousPayoutsTotal = 0
        let periodCandidateValue: number | undefined
        let periodComparisonData:
          | {
              attainment: number
              tier_attainment: number
              commission_percentage: number
              field_value: number
              compensation_value: number
            }
          | undefined

        if (this.hasLongTermBalancing() && this.flowExecutor.period) {
          const previousPayouts = await this.getPreviousPayoutsForLongTermBalancing(
            this.flowExecutor.profileId
          )
          previousPayoutsTotal = this.sumPreviousPayoutsForLongTermBalancing(previousPayouts)

          if (this.shouldApplyLongTermOnlyIfHigher()) {
            const currentPeriod = this.getComputationPeriod(true)
            let nonLtbAttainment = await this.getAttainment(currentPeriod, 'period_candidate')
            nonLtbAttainment = isTieredCommissionWithTargets(this.formData)
              ? nonLtbAttainment * 100
              : nonLtbAttainment
            const nonLtbTier = nonLtbTiers.find(
              tierDefinition =>
                nonLtbAttainment >= tierDefinition.attainment ||
                (nonLtbAttainment < tierDefinition.attainment && tierDefinition.attainment === 0)
            )
            let nonLtbCommissionPercentage = 0
            let nonLtbFieldValue = 0
            if (nonLtbTier) {
              nonLtbFieldValue = await this.getTieredFieldValue(currentPeriod)
              nonLtbCommissionPercentage =
                getCompensationValue(
                  nonLtbTier.value ?? Number(nonLtbTier.percentage) * 100,
                  nonLtbAttainment
                ) / 100
              periodComparisonData = {
                attainment: nonLtbAttainment,
                tier_attainment: nonLtbTier.attainment,
                commission_percentage: nonLtbCommissionPercentage,
                field_value: nonLtbFieldValue,
                compensation_value: nonLtbCommissionPercentage * nonLtbFieldValue
              }
              const nonLtbAscendingTiers = [...nonLtbTiers].sort(
                (a, b) => a.attainment - b.attainment
              )
              const nonLtbTierIndex = nonLtbAscendingTiers.findIndex(
                tierDefinition => tierDefinition === nonLtbTier
              )
              console.table({
                'Non-LTB Tier Index': nonLtbTierIndex,
                'Non-LTB Tier Attainment': nonLtbTier.attainment,
                'Non-LTB Attainment': nonLtbAttainment
              })
            }

            periodCandidateValue = nonLtbCommissionPercentage * nonLtbFieldValue
          }
        }

        const ltbAscendingTiers = [...ltbTiers].sort((a, b) => a.attainment - b.attainment)
        const tierIndex = ltbAscendingTiers.findIndex(tierDefinition => tierDefinition === tier)
        console.table({
          'Tier Index': tierIndex,
          'Tier Attainment': tier.attainment,
          Attainment: attainment
        })

        const balancingResults: CommissionOutputField[] = this.hasLongTermBalancing()
          ? [
              {
                name: 'total_previous_payouts',
                value: previousPayoutsTotal
              },
              {
                name: 'compensation_value_before_regulation',
                value: valueBeforeRegulation
              },
              {
                name: 'regulation_period',
                value: this.flowExecutor.compensationRule.long_term_balancing_period
              },
              ...(periodComparisonData
                ? [
                    {
                      name: 'compensation_value_comparison',
                      value: {
                        long_term_balancing: {
                          attainment,
                          tier_attainment: tier.attainment,
                          commission_percentage: commissionPercentage,
                          field_value: fieldValue,
                          compensation_value_before_regulation: valueBeforeRegulation,
                          compensation_value: valueBeforeRegulation - previousPayoutsTotal
                        },
                        period: periodComparisonData
                      }
                    } satisfies CommissionOutputField
                  ]
                : [])
            ]
          : []
        const displayedValueField: CommissionOutputField = {
          name: this.flowExecutor.compensationRule.outcome_period ? 'metric_value' : 'field_value',
          value: fieldValue
        }

        return [
          {
            name: 'tier_index',
            value: tierIndex
          },
          {
            name: 'attainment',
            value: attainment
          },
          {
            name: 'commission_percentage',
            value: commissionPercentage
          },
          {
            name: 'compensation_value',
            value: valueBeforeRegulation
          },
          ...(marginalCommissionTiering.length > 0
            ? [
                {
                  name: 'marginal_commission_tiering',
                  value: marginalCommissionTiering
                } satisfies CommissionOutputField
              ]
            : []),
          ...(periodCandidateValue !== undefined
            ? [
                {
                  name: 'compensation_value_period_candidate',
                  value: periodCandidateValue
                } satisfies CommissionOutputField
              ]
            : []),
          displayedValueField,
          ...balancingResults
        ]
      }
    }

    const fieldValue = await this.getTieredFieldValue(period)
    const displayedValueField: CommissionOutputField = {
      name: this.flowExecutor.compensationRule.outcome_period ? 'metric_value' : 'field_value',
      value: fieldValue
    }

    return [
      { name: 'tier_index', value: -1 },
      { name: 'attainment', value: attainment },
      { name: 'commission_percentage', value: 0 },
      { name: 'compensation_value', value: 0 },
      displayedValueField
    ]
  }

  private async getTieredFieldValue(period?: { start: string; end: string }) {
    if (
      this.flowExecutor.compensationRule.outcome_period &&
      period &&
      hasCalculateOverMetric(this.formData)
    ) {
      const creditSetupTargetScope = isTieredCommissionWithTargets(this.formData)
        ? await this.getCreditSetupTargetScope(period.end)
        : undefined

      return this.getMetricReferenceValue(
        this.formData.calculate_over_metric,
        period.start,
        period.end,
        undefined,
        creditSetupTargetScope
      )
    }

    return hasCalculateOverColumn(this.formData)
      ? ((this.flowExecutor.recordData!.getFieldValue(
          this.formData.calculate_over_column.name as string,
          this.flowExecutor.customFields
        ) || 0) as number)
      : 0
  }

  private async processTvpBasedTieredCommission(): Promise<CommissionOutput> {
    let attainment = 0
    const processingDate = this.getProcessingDate()

    if (!processingDate) {
      console.warn(
        'Processing date not found: ',
        this.flowExecutor.compensationRule.accrual_date_field
      )
      return [
        {
          name: 'compensation_value',
          value: 0
        }
      ]
    }

    const profileId = this.getProfileId()

    if (!profileId) {
      return this.returnZeroCompensation('Profile ID not found for TVP-based tiered commission')
    }

    const currentYear = this.flowExecutor.compensationRule.outcome_period
      ? new Date(this.flowExecutor.period!.start).getFullYear()
      : new Date(processingDate).getFullYear()

    const { tvpAllocation, yearlyTargetAmount } = await this.calculateTvpCommissionPercentage(
      profileId,
      currentYear
    )

    if (!tvpAllocation || !yearlyTargetAmount || yearlyTargetAmount === 0) {
      return this.returnZeroCompensation('TVP allocation or yearly target not found')
    }

    const period = this.flowExecutor.compensationRule.outcome_period
      ? this.getComputationPeriod()
      : undefined

    attainment = await this.getAttainment(period, 'ltb')
    attainment = isTieredCommissionWithTargets(this.formData) ? attainment * 100 : attainment

    const tierFormData = this.formData as CommissionFormDataTiered & {
      tiers_without_ltb?: CommissionFormDataTiered['tiers']
    }
    const tiers = [
      ...((this.formData as CommissionFormDataTiered).tiers as {
        attainment: number
        value: number | string
        percentage?: number | string
      }[])
    ].sort((a, b) => b.attainment - a.attainment)

    for (const tier of tiers) {
      if (
        attainment >= tier.attainment ||
        (attainment < tier.attainment && tier.attainment === 0)
      ) {
        if (!tier.value) {
          return [
            {
              name: 'compensation_value',
              value: 0
            }
          ]
        }

        const commissionRate =
          (getCompensationValue(tier.value, attainment) * tvpAllocation.allocated_amount) /
          yearlyTargetAmount /
          100
        const ltbTiers = [...tierFormData.tiers].sort((a, b) => b.attainment - a.attainment)
        const nonLtbTiers = [...(tierFormData.tiers_without_ltb ?? tierFormData.tiers)].sort(
          (a, b) => b.attainment - a.attainment
        )

        const tvpScope = this.flowExecutor.compensationRule.outcome_period
          ? await this.getTargetCreditedMetricScope(profileId, processingDate)
          : null
        if (
          this.flowExecutor.compensationRule.outcome_period &&
          hasCalculateOverMetric(this.formData) &&
          !tvpScope
        ) {
          return this.returnZeroCompensation('TVP target or beneficiary team not found')
        }
        const metricCurrency = tvpScope?.targetCurrency

        const fieldValue =
          this.flowExecutor.compensationRule.outcome_period && hasCalculateOverMetric(this.formData)
            ? await this.getMetricReferenceValue(
                this.formData.calculate_over_metric,
                period?.start ?? this.flowExecutor.period!.start,
                period?.end ?? this.flowExecutor.period!.end,
                undefined,
                tvpScope ?? undefined
              )
            : hasCalculateOverColumn(this.formData)
              ? ((this.flowExecutor.recordData!.getFieldValue(
                  this.formData.calculate_over_column.name as string,
                  this.flowExecutor.customFields
                ) || 0) as number)
              : 0

        const valueBeforeRegulation = commissionRate * fieldValue
        let previousPayoutsTotal = 0
        let periodCandidateValue: number | undefined
        let periodComparisonData:
          | {
              attainment: number
              tier_attainment: number
              commission_percentage: number
              field_value: number
              compensation_value: number
            }
          | undefined

        if (this.hasLongTermBalancing() && this.flowExecutor.period) {
          const previousPayouts = await this.getPreviousPayoutsForLongTermBalancing(
            this.flowExecutor.profileId
          )
          previousPayoutsTotal = this.sumPreviousPayoutsForLongTermBalancing(previousPayouts)

          if (this.shouldApplyLongTermOnlyIfHigher()) {
            const currentPeriod = this.getComputationPeriod(true)
            let nonLtbAttainment = await this.getAttainment(currentPeriod, 'period_candidate')
            nonLtbAttainment = isTieredCommissionWithTargets(this.formData)
              ? nonLtbAttainment * 100
              : nonLtbAttainment
            const nonLtbTier = nonLtbTiers.find(
              tierDefinition =>
                nonLtbAttainment >= tierDefinition.attainment ||
                (nonLtbAttainment < tierDefinition.attainment && tierDefinition.attainment === 0)
            )
            let nonLtbCommissionRate = 0
            let nonLtbFieldValue = 0
            if (nonLtbTier) {
              nonLtbCommissionRate =
                (getCompensationValue(
                  nonLtbTier.value ?? Number(nonLtbTier.percentage) * 100,
                  nonLtbAttainment
                ) *
                  tvpAllocation.allocated_amount) /
                yearlyTargetAmount /
                100
              nonLtbFieldValue =
                this.flowExecutor.compensationRule.outcome_period &&
                hasCalculateOverMetric(this.formData)
                  ? await this.getMetricReferenceValue(
                      this.formData.calculate_over_metric,
                      currentPeriod.start,
                      currentPeriod.end,
                      undefined,
                      tvpScope ?? undefined
                    )
                  : hasCalculateOverColumn(this.formData)
                    ? ((this.flowExecutor.recordData!.getFieldValue(
                        this.formData.calculate_over_column.name as string,
                        this.flowExecutor.customFields
                      ) || 0) as number)
                    : 0
              periodComparisonData = {
                attainment: nonLtbAttainment,
                tier_attainment: nonLtbTier.attainment,
                commission_percentage: nonLtbCommissionRate,
                field_value: nonLtbFieldValue,
                compensation_value: nonLtbCommissionRate * nonLtbFieldValue
              }
            }

            periodCandidateValue = nonLtbCommissionRate * nonLtbFieldValue
          }
        }

        const ltbAscendingTiers = [...ltbTiers].sort((a, b) => a.attainment - b.attainment)
        const tierIndex = ltbAscendingTiers.findIndex(t => t === tier)

        const balancingResults: CommissionOutputField[] = this.hasLongTermBalancing()
          ? [
              {
                name: 'total_previous_payouts',
                value: previousPayoutsTotal
              },
              {
                name: 'compensation_value_before_regulation',
                value: valueBeforeRegulation
              },
              {
                name: 'regulation_period',
                value: this.flowExecutor.compensationRule.long_term_balancing_period
              },
              ...(periodComparisonData
                ? [
                    {
                      name: 'compensation_value_comparison',
                      value: {
                        long_term_balancing: {
                          attainment,
                          tier_attainment: tier.attainment,
                          commission_percentage: commissionRate,
                          field_value: fieldValue,
                          compensation_value_before_regulation: valueBeforeRegulation,
                          compensation_value: valueBeforeRegulation - previousPayoutsTotal
                        },
                        period: periodComparisonData
                      }
                    } satisfies CommissionOutputField
                  ]
                : [])
            ]
          : []

        return [
          {
            name: 'tier_index',
            value: tierIndex
          },
          {
            name: 'attainment',
            value: attainment
          },
          {
            name: 'commission_percentage',
            value: commissionRate
          },
          {
            name: 'compensation_value',
            value: valueBeforeRegulation
          },
          ...(periodCandidateValue !== undefined
            ? [
                {
                  name: 'compensation_value_period_candidate',
                  value: periodCandidateValue
                } satisfies CommissionOutputField
              ]
            : []),
          {
            name: this.flowExecutor.compensationRule.outcome_period
              ? 'metric_value'
              : 'field_value',
            value: fieldValue
          },
          ...(metricCurrency
            ? [
                {
                  name: 'metric_currency',
                  value: metricCurrency
                } satisfies CommissionOutputField
              ]
            : []),
          {
            name: 'tvp_allocation',
            value: tvpAllocation.allocated_amount
          },
          {
            name: 'tvp_allocation_before_proration',
            value: tvpAllocation.allocated_amount_before_proration
          },
          {
            name: 'tvp_proration_factor',
            value: tvpAllocation.proration_factor
          },
          {
            name: 'tvp_percentage',
            value: tvpAllocation.percentage
          },
          {
            name: 'tvp_total',
            value:
              tvpAllocation.percentage === 0
                ? 0
                : tvpAllocation.allocated_amount_before_proration / tvpAllocation.percentage
          },
          {
            name: 'yearly_target_amount',
            value: yearlyTargetAmount
          },
          ...balancingResults
        ]
      }
    }

    return [
      {
        name: 'compensation_value',
        value: 0
      }
    ]
  }

  private async processTvpBasedStraightCommission(): Promise<CommissionOutput> {
    if (!this.flowExecutor.compensationRule.outcome_period) {
      return await this.processTvpBasedStraightCommissionNonPeriod()
    }

    return await this.processTvpBasedStraightCommissionByPeriod()
  }

  private async processTvpBasedStraightCommissionByPeriod(): Promise<CommissionOutput> {
    const profileId = this.flowExecutor.profileId
    if (!profileId) {
      return this.returnZeroCompensation('Profile ID not found for TVP-based commission')
    }
    if (!hasCalculateOverMetric(this.formData)) {
      return this.returnZeroCompensation(
        'TVP-based straight commission requires calculate_over_metric'
      )
    }
    const formData = this.formData

    const currentYear = new Date(this.flowExecutor.period!.start).getFullYear()
    const { commissionPercentage, tvpAllocation, yearlyTargetAmount } =
      await this.calculateTvpCommissionPercentage(profileId, currentYear)

    if (commissionPercentage == null) {
      return this.returnZeroCompensation()
    }

    const tvpScope = await this.getTargetCreditedMetricScope(
      profileId,
      this.flowExecutor.period!.end
    )
    if (!tvpScope) {
      return this.returnZeroCompensation('TVP target or beneficiary team not found')
    }
    const metricCurrency = tvpScope.targetCurrency
    const period = this.getComputationPeriod()
    const metricValue = await this.getMetricReferenceValue(
      formData.calculate_over_metric,
      period.start,
      period.end,
      undefined,
      tvpScope
    )

    const valueBeforeRegulation = commissionPercentage * metricValue
    let previousPayoutsTotal = 0
    let periodCandidateValue: number | undefined
    let periodComparisonData:
      | {
          field_value: number
          compensation_value: number
          period: { start: string; end: string }
        }
      | undefined

    if (this.hasLongTermBalancing()) {
      const previousPayouts = await this.getPreviousPayoutsForLongTermBalancing(
        this.flowExecutor.profileId
      )
      previousPayoutsTotal = this.sumPreviousPayoutsForLongTermBalancing(previousPayouts)

      if (this.shouldApplyLongTermOnlyIfHigher()) {
        const currentPeriod = this.getComputationPeriod(true)
        const nonLtbMetricValue = await this.getMetricReferenceValue(
          formData.calculate_over_metric,
          currentPeriod.start,
          currentPeriod.end,
          undefined,
          tvpScope
        )
        const nonLtbValue = commissionPercentage * nonLtbMetricValue
        periodComparisonData = {
          field_value: nonLtbMetricValue,
          compensation_value: nonLtbValue,
          period: currentPeriod
        }
        periodCandidateValue = nonLtbValue
      }
    }

    const balancingResults: CommissionOutputField[] = this.hasLongTermBalancing()
      ? [
          {
            name: 'total_previous_payouts',
            value: previousPayoutsTotal
          },
          {
            name: 'compensation_value_before_regulation',
            value: valueBeforeRegulation
          },
          {
            name: 'regulation_period',
            value: this.flowExecutor.compensationRule.long_term_balancing_period
          },
          ...(periodComparisonData
            ? [
                {
                  name: 'compensation_value_comparison',
                  value: {
                    long_term_balancing: {
                      field_value: metricValue,
                      compensation_value_before_regulation: valueBeforeRegulation,
                      compensation_value: valueBeforeRegulation - previousPayoutsTotal,
                      period
                    },
                    period: periodComparisonData
                  }
                } satisfies CommissionOutputField
              ]
            : [])
        ]
      : []

    return [
      {
        name: 'commission_percentage',
        value: commissionPercentage
      },
      {
        name: 'compensation_value',
        value: valueBeforeRegulation
      },
      ...(periodCandidateValue !== undefined
        ? [
            {
              name: 'compensation_value_period_candidate',
              value: periodCandidateValue
            } satisfies CommissionOutputField
          ]
        : []),
      {
        name: 'field_value',
        value: metricValue
      },
      {
        name: 'metric_currency',
        value: metricCurrency
      },
      {
        name: 'tvp_allocation',
        value: tvpAllocation!.allocated_amount
      },
      {
        name: 'tvp_allocation_before_proration',
        value: tvpAllocation!.allocated_amount_before_proration
      },
      {
        name: 'tvp_proration_factor',
        value: tvpAllocation!.proration_factor
      },
      {
        name: 'tvp_percentage',
        value: tvpAllocation!.percentage
      },
      {
        name: 'tvp_total',
        value:
          tvpAllocation!.percentage === 0
            ? 0
            : tvpAllocation!.allocated_amount_before_proration / tvpAllocation!.percentage
      },
      {
        name: 'yearly_target_amount',
        value: yearlyTargetAmount!
      },
      ...balancingResults,
      {
        name: 'context',
        value: {
          attainment: metricValue,
          metric: (this.formData as CommissionFormDataTieredMetric).calculate_over_metric,
          period,
          tvp_based: true,
          tvp_allocation: tvpAllocation!.allocated_amount,
          tvp_allocation_before_proration: tvpAllocation!.allocated_amount_before_proration,
          tvp_proration_factor: tvpAllocation!.proration_factor,
          yearly_target: yearlyTargetAmount!
        }
      }
    ]
  }

  private async processTvpBasedStraightCommissionNonPeriod(): Promise<CommissionOutput> {
    const profileId = this.getProfileId()

    if (!profileId) {
      return this.returnZeroCompensation('Profile ID not found in record for TVP-based commission')
    }

    const processingDate = this.getRecordProcessingDate()
    if (!processingDate) {
      return this.returnZeroCompensation('Processing date not found for TVP-based commission')
    }

    const currentYear = new Date(processingDate).getFullYear()
    const { commissionPercentage, tvpAllocation, yearlyTargetAmount } =
      await this.calculateTvpCommissionPercentage(profileId, currentYear)

    if (commissionPercentage == null) {
      return this.returnZeroCompensation()
    }

    let columnName: string | null = null
    const targetId = (this.formData as CommissionFormDataTieredTarget).targets?.[0]?.id
    if (targetId) {
      const target = await this.flowExecutor.rpcClient.getTarget(targetId)
      if (target?.metrics?.formula) {
        columnName = Metrics.getColumnFromMetricFormula(
          target.metrics.formula,
          target.metrics.source,
          this.flowExecutor.source
        )
      }
    }

    if (!columnName) {
      return this.returnZeroCompensation('No column found to calculate commission over')
    }

    const fieldValue = (this.flowExecutor.recordData!.getFieldValue(
      columnName,
      this.flowExecutor.customFields
    ) || 0) as number

    return this.buildTvpCommissionOutput(
      commissionPercentage,
      fieldValue,
      tvpAllocation!.percentage,
      tvpAllocation!.allocated_amount,
      tvpAllocation!.allocated_amount_before_proration,
      tvpAllocation!.proration_factor,
      yearlyTargetAmount!
    )
  }

  private async calculateTvpCommissionPercentage(
    profileId: string,
    year: number
  ): Promise<{
    commissionPercentage: number | null
    tvpAllocation: {
      percentage: number
      allocated_amount: number
      allocated_amount_before_proration: number
      proration_factor: number
    } | null
    yearlyTargetAmount: number | null
  }> {
    const yearStart = `${year}-01-01`
    const yearEnd = `${year}-12-31`

    const tvpDomain = new Tvp(this.flowExecutor.rpcClient)
    const tvpAllocation = await tvpDomain.getTvpCommissionAllocation(
      profileId,
      this.flowExecutor.compensationRule.compensation_rule_versions[0],
      this.flowExecutor.organizationId,
      yearStart,
      yearEnd
    )

    if (!tvpAllocation) {
      console.warn(
        `No TVP allocation found for profile ${profileId} and comp rule ${this.flowExecutor.compensationRule.id}`
      )
      return {
        commissionPercentage: null,
        tvpAllocation: null,
        yearlyTargetAmount: null
      }
    }

    if (
      !(this.formData as CommissionFormDataTieredTarget).targets ||
      (this.formData as CommissionFormDataTieredTarget).targets.length === 0
    ) {
      console.warn('No target configured for TVP-based commission')
      return {
        commissionPercentage: null,
        tvpAllocation,
        yearlyTargetAmount: 0
      }
    }

    const targetData = await this.flowExecutor.rpcClient.getPeriodTargetAmount(
      (this.formData as CommissionFormDataTieredTarget).targets![0].id,
      yearStart,
      yearEnd,
      profileId
    )

    const yearlyTargetAmount = targetData.total_amount

    if (yearlyTargetAmount === 0) {
      console.warn('Yearly target amount is 0 for TVP-based commission')
      return {
        commissionPercentage: null,
        tvpAllocation,
        yearlyTargetAmount: 0
      }
    }

    const commissionPercentage = tvpAllocation.allocated_amount / yearlyTargetAmount
    return { commissionPercentage, tvpAllocation, yearlyTargetAmount }
  }

  private returnZeroCompensation(message?: string): CommissionOutput {
    if (message) console.warn(message)
    return [{ name: 'compensation_value', value: 0 }]
  }

  private buildTvpCommissionOutput(
    commissionPercentage: number,
    value: number,
    tvpPercentage: number,
    tvpAllocation: number,
    tvpAllocationBeforeProration: number,
    tvpProrationFactor: number,
    yearlyTargetAmount: number,
    context?: {
      attainment: number
      metric?: MetricRef
      period: { start: string; end: string }
      tvp_based?: boolean
      tvp_allocation?: number
      tvp_allocation_before_proration?: number
      tvp_proration_factor?: number
      yearly_target?: number
    }
  ): CommissionOutput {
    const output: CommissionOutput = [
      { name: 'commission_percentage', value: commissionPercentage },
      { name: 'compensation_value', value: commissionPercentage * value },
      { name: 'field_value', value },
      { name: 'tvp_allocation', value: tvpAllocation },
      { name: 'tvp_allocation_before_proration', value: tvpAllocationBeforeProration },
      { name: 'tvp_proration_factor', value: tvpProrationFactor },
      { name: 'tvp_percentage', value: tvpPercentage },
      {
        name: 'tvp_total',
        value: tvpPercentage === 0 ? 0 : tvpAllocationBeforeProration / tvpPercentage
      },
      { name: 'yearly_target_amount', value: yearlyTargetAmount }
    ]

    if (context) {
      output.push({
        name: 'context',
        value: {
          ...context,
          tvp_based: true,
          tvp_allocation: tvpAllocation,
          tvp_allocation_before_proration: tvpAllocationBeforeProration,
          tvp_proration_factor: tvpProrationFactor,
          yearly_target: yearlyTargetAmount
        }
      })
    }

    return output
  }

  async parse() {
    return this.node
  }

  static validate(
    node: TypedNode<'commission'>,
    diagram: DolfinDiagram
  ): {
    errors: NodeError[]
    warnings: NodeWarning[]
  } {
    const superValidation = super.validate(node, diagram)

    return {
      errors: [...superValidation.errors],
      warnings: [...superValidation.warnings]
    }
  }
}
